// lib/api.ts

import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  CallList,
  CallCreateRequest,
  CallResponse,
  MainSystemPrompt,
  MainSystemPromptCreate,
  OrganizationCreate,
  OrganizationWithUsers,
  OutboundCallRequest,
  StrategyCreate,
  StrategyUpdate,
  StrategyVersionCreate,
  StrategyVersion,
  StrategyWithVersions,
  User,
  VerifiedPhoneNumber,
  CallLog,
  CallCreateList,
  Calls,
  CallTranscript,
  PostCallWorkflowOutputs,
  TokenResponse,
  TokenCreate,
  TokenList,
  TokenUpdate,
  InboundCallStrategyVersionCreate,
  InboundCallStrategyVersion,
  InboundPhoneNumber,
  TranslationRequest,
  TranslationResponse,
  CallQueryResponse,
  CallQueryParams,
} from "@/lib/types";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.accessToken?.toString();
      if (idToken) {
        config.headers["Authorization"] = `Bearer ${idToken}`;
      }
    } catch (error) {
      console.error("Error getting auth token:", error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

async function apiCall<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response = await axiosInstance(config);
    return response.data;
  } catch (error) {
    console.error("API call failed:", error);
    throw error;
  }
}

async function streamingApiCall(
  config: AxiosRequestConfig,
  onChunk: (chunk: string) => void
): Promise<void> {
  let buffer = "";

  try {
    const response = await axiosInstance({
      ...config,
      responseType: "text",
      headers: {
        ...config.headers,
        Accept: "text/event-stream",
      },
      onDownloadProgress: (progressEvent: any) => {
        // Access the XMLHttpRequest directly through currentTarget
        const xhr = progressEvent.event.currentTarget;
        if (!xhr || !xhr.responseText) return;

        // Get only the new part of the response
        const newText = xhr.responseText.substring(buffer.length);
        buffer = xhr.responseText;

        if (!newText) return;

        // Process each line of the new text
        const lines = newText.split("\n");
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          // Remove "data: " prefix if it exists
          const cleanedChunk = trimmed.startsWith("data:")
            ? trimmed.substring(6)
            : trimmed;

          onChunk(cleanedChunk);
        }
      },
    });

    console.log("Stream complete");
  } catch (error) {
    console.error("Streaming API call failed:", error);
    throw error;
  }
}

export const api = {
  // Daily
  testMessage: () =>
    apiCall<string>({
      url: "/daily/test-message",
      method: "POST",
    }),

  dailyStartBot: () =>
    apiCall<string>({
      url: "/daily/daily_start_bot",
      method: "POST",
    }),

  outboundCall: (data: OutboundCallRequest) =>
    apiCall<string>({
      url: "/daily/outbound_call",
      method: "POST",
      data,
    }),

  // Health
  healthCheck: () =>
    apiCall<string>({
      url: "/health",
      method: "GET",
    }),

  secretCheck: () =>
    apiCall<string>({
      url: "/health/secret-check",
      method: "GET",
    }),

  // User
  getOrCreateUser: () =>
    apiCall<User>({
      url: "/user",
      method: "POST",
    }),

  getUser: () =>
    apiCall<User>({
      url: "/user/",
      method: "GET",
    }),

  // Organization
  listOrganizations: () =>
    apiCall<OrganizationWithUsers[]>({
      url: "/organization",
      method: "GET",
    }),

  createOrganization: (data: OrganizationCreate) =>
    apiCall<OrganizationWithUsers>({
      url: "/organization",
      method: "POST",
      data,
    }),

  addUserToOrganization: (organizationId: string, userEmail: string) =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}/add-user`,
      method: "POST",
      params: { user_email: userEmail },
    }),

  getOrganization: (organizationId: string) =>
    apiCall<OrganizationWithUsers>({
      url: `/organization/${organizationId}`,
      method: "GET",
    }),

  getOrganizationCalls: (organizationId: string, params?: CallQueryParams) =>
    apiCall<CallQueryResponse>({
      url: `/organization/${organizationId}/calls/v2`,
      method: "GET",
      params: {
        limit: params?.limit || 100,
        offset: params?.offset || 0,
        strategy_version_id: params?.strategy_version_id,
        strategy_id: params?.strategy_id,
        patient_id: params?.patient_id,
        callee_phone_number: params?.callee_phone_number,
        strategy_type: params?.strategy_type,
        call_status: params?.call_status,
        call_started_after: params?.call_started_after,
        call_started_before: params?.call_started_before,
        call_ended_after: params?.call_ended_after,
        call_ended_before: params?.call_ended_before,
        sort_by: params?.sort_by || "created_at",
        sort_order: params?.sort_order || "desc",
      },
    }),

  verifyPhoneNumber: (id: string, phoneNumber: string) =>
    apiCall<VerifiedPhoneNumber>({
      url: `/organization/${id}/verify-phone`,
      method: "POST",
      params: { phone_number: phoneNumber },
    }),

  getVerifiedPhoneNumbers: (organizationId: string) =>
    apiCall<VerifiedPhoneNumber[]>({
      url: `/organization/${organizationId}/verified-phone-numbers`,
      method: "GET",
    }),

  // Strategy
  getStrategy: (id: string) =>
    apiCall<StrategyWithVersions>({
      url: `/strategy/${id}`,
      method: "GET",
    }),

  updateStrategy: (id: string, data: StrategyUpdate) =>
    apiCall<StrategyWithVersions>({
      url: `/strategy/${id}`,
      method: "PUT",
      data,
    }),

  renameStrategy: (id: string, newName: string) =>
    apiCall<StrategyWithVersions>({
      url: `/strategy/${id}/rename`,
      method: "PATCH",
      data: { new_name: newName },
    }),

  getOrganizationStrategies: (orgId: string, skip = 0, limit = 100) =>
    apiCall<StrategyWithVersions[]>({
      url: `/strategy/organization/${orgId}/strategies`,
      method: "GET",
      params: { skip, limit },
    }),

  createStrategy: (data: StrategyCreate) =>
    apiCall<StrategyWithVersions>({
      url: "/strategy",
      method: "POST",
      data,
    }),

  createStrategyVersion: (id: string, data: StrategyVersionCreate) =>
    apiCall<StrategyVersion>({
      url: `/strategy/${id}/version`,
      method: "POST",
      data,
    }),

  // Strategy Version
  getStrategyVersion: (id: string) =>
    apiCall<StrategyVersion>({
      url: `/strategy-version/${id}`,
      method: "GET",
    }),

  renameStrategyVersion: (id: string, newDescription: string) =>
    apiCall<StrategyVersion>({
      url: `/strategy-version/${id}/rename`,
      method: "PATCH",
      data: { new_description: newDescription },
    }),

  // Main System Prompt
  getLatestMainSystemPrompt: () =>
    apiCall<MainSystemPrompt>({
      url: "/main-system-prompt",
      method: "GET",
    }),

  createMainSystemPrompt: (data: MainSystemPromptCreate) =>
    apiCall<MainSystemPrompt>({
      url: "/main-system-prompt",
      method: "POST",
      data,
    }),

  getMainSystemPrompt: (id: string) =>
    apiCall<MainSystemPrompt>({
      url: `/main-system-prompt/${id}`,
      method: "GET",
    }),

  getAllMainSystemPrompts: () =>
    apiCall<MainSystemPrompt[]>({
      url: "/main-system-prompt/all",
      method: "GET",
    }),

  // Call
  createCall: (data: CallCreateRequest) =>
    apiCall<CallResponse>({
      url: "/call",
      method: "POST",
      data,
    }),

  createBulkCalls: (data: CallCreateList) =>
    apiCall<Calls>({
      url: "/call/multiple",
      method: "POST",
      data,
    }),

  getCall: (id: string) =>
    apiCall<CallResponse>({
      url: `/call/${id}`,
      method: "GET",
    }),

  startCall: (id: string) =>
    apiCall<string>({
      url: `/call/${id}/start`,
      method: "POST",
    }),

  cloneCall: (id: string) =>
    apiCall<CallResponse>({
      url: `/call/${id}/clone`,
      method: "POST",
    }),

  getCallRecording: async (id: string): Promise<Blob> => {
    const response = await axiosInstance.get<Blob>(`/call/${id}/recording`, {
      responseType: "blob",
    });
    return response.data;
  },

  getCallLog: (id: string) =>
    apiCall<CallLog>({
      url: `/call/${id}/log`,
      method: "GET",
    }),

  // Root
  root: () =>
    apiCall<string>({
      url: "/hi",
      method: "GET",
    }),
  getCallTranscript: (id: string) =>
    apiCall<CallTranscript>({
      url: `/call/${id}/transcription`,
      method: "GET",
    }),

  getPostCallWorkflowOutputs: (id: string) =>
    apiCall<PostCallWorkflowOutputs>({
      url: `/call/${id}/post-call-workflow/outputs`,
      method: "GET",
    }),
  streamChatCompletion: (messages: any[], onChunk: (chunk: string) => void) =>
    streamingApiCall(
      {
        url: "/chat/completions",
        method: "POST",
        data: {
          messages,
          model: "gpt-4",
          stream: true,
          temperature: 0.7,
        },
      },
      onChunk
    ),

  // Settings
  generateToken: (data: TokenCreate) =>
    apiCall<TokenResponse>({
      url: "/token/",
      method: "POST",
      data,
    }),

  revokeToken: (id: string) =>
    apiCall<TokenResponse>({
      url: `/token/revoke/${id}`,
      method: "DELETE",
    }),

  listTokens: (organizationId: string | null) =>
    apiCall<TokenList>({
      url: `/token/list`,
      method: "GET",
      params: { organization_id: organizationId },
    }),

  updateToken: (id: string, data: TokenUpdate) =>
    apiCall<TokenResponse>({
      url: `/token/${id}`,
      method: "PATCH",
      data,
    }),

  // Inbound Call
  createInboundCallStrategyVersion: (data: InboundCallStrategyVersionCreate) =>
    apiCall<InboundCallStrategyVersion>({
      url: "/inbound-call/inbound-call-strategy-version",
      method: "POST",
      data,
    }),

  getOrganizationInboundPhoneNumbers: (organizationId: string) =>
    apiCall<InboundPhoneNumber[]>({
      url: `/organization/${organizationId}/incoming-phone-numbers`,
      method: "GET",
    }),

  // Translation
  translateText: (data: TranslationRequest) =>
    apiCall<TranslationResponse>({
      url: "/translate",
      method: "POST",
      data,
    }),
};

export default api;
